import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Store, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, formatCompressionStats } from '../utils/imageCompression';
import { ImageWithFallback } from './ui/image-with-fallback';

/**
 * Reusable logo upload field. Holds local state, returns the public URL via onChange.
 * Props:
 *   api: axios instance
 *   value: current logo URL (string)
 *   onChange: (newUrl: string) => void
 *   testIdPrefix: optional
 */
export function BusinessLogoField({ api, value, onChange, testIdPrefix = 'logo', businessName = '' }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const before = f.size;
      const compressed = await compressImage(f, { maxSide: 512, quality: 0.85 });
      if (compressed.size < before) {
        toast.message(`Logo compressed: ${formatCompressionStats(before, compressed.size)}`);
      }
      const fd = new FormData();
      fd.append('file', compressed);
      fd.append('purpose', 'merchant-logo');
      if (businessName) fd.append('subject_name', businessName);
      const up = await api.post('/api/upload/member-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = up.data.url || up.data.file_url;
      onChange(url);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const clear = () => onChange('');

  return (
    <div>
      <Label className="text-[#A0B4CC] text-xs">Business Logo</Label>
      <div className="flex items-center gap-3 mt-1">
        <div className="w-16 h-16 rounded-2xl bg-[#000B18] border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {value
            ? <ImageWithFallback
                src={value}
                alt="Logo"
                className="w-full h-full object-cover"
                fallback={<Store className="w-7 h-7 text-[#5C7599]" />}
              />
            : <Store className="w-7 h-7 text-[#5C7599]" />
          }
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload}
          data-testid={`${testIdPrefix}-input`} />
        <div className="flex gap-2 flex-1">
          <Button type="button" onClick={pick} disabled={uploading}
            variant="outline"
            className="border-white/20 text-[#A0B4CC]"
            data-testid={`${testIdPrefix}-upload-btn`}>
            {uploading
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
              : <><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> {value ? 'Replace' : 'Upload'}</>}
          </Button>
          {value && (
            <Button type="button" onClick={clear} variant="ghost"
              className="text-[#FF1744] hover:bg-[#FF1744]/10"
              data-testid={`${testIdPrefix}-clear-btn`}>
              Clear
            </Button>
          )}
        </div>
      </div>
      <p className="text-[10px] text-[#5C7599] mt-1">Recommended: 512×512 px square · PNG or JPG</p>
    </div>
  );
}
